import {cn} from 'platform/shared-tw';
import {match} from 'ts-pattern';

import type {CSSProperties} from 'react';
import {useTranslation} from 'react-i18next';

import {isNotNil} from 'ramda';

import {TABLE_GRID_COLUMNS_CLASS} from '../Table/Table';
import type {CarPartModalProps, PartOperation, PartOperationVariant} from './CarPartModal.types';
import {OperationRow} from './OperationRow';
import {PartIllustration} from './PartIllustration';
import {getPartIllustration} from './partIllustrations';
import {useEscapeKey} from './useEscapeKey';

type CSSPropertiesWithVars = CSSProperties & Record<`--${string}`, string>;

const OPERATIONS_GRID_STYLE: CSSPropertiesWithVars = {
  '--columns-template': 'minmax(0,1fr)',
};

const DIAGRAM_CHROME_OFFSET_CLASS = 'pt-32';

const VARIANT_DOT_CLASS: Record<PartOperationVariant, string> = {
  damage: 'bg-(--color-mira-red-50)',
  service: 'bg-(--color-mira-orange-50)',
  suggestions: 'bg-(--color-mira-purple-50)',
};

const VARIANT_PRIORITY: Record<PartOperationVariant, number> = {
  damage: 3,
  service: 2,
  suggestions: 1,
};

// Mirrors ROW_TYPE_THRESHOLDS.DAMAGE * 100 in @mira/report; kept in lockstep manually (ui must not depend on report).
const LIKELY_FINE_PERCENT_THRESHOLD = 80;

function getDominantVariant(operations: readonly PartOperation[]): PartOperationVariant | null {
  let max = 0;
  let dominant: PartOperationVariant | null = null;

  for (const op of operations) {
    const priority = VARIANT_PRIORITY[op.variant];
    if (priority > max) {
      max = priority;
      dominant = op.variant;
    }
  }

  return dominant;
}

function getDominantSubtitle(
  dominant: PartOperationVariant,
  operations: readonly PartOperation[],
  t: ReturnType<typeof useTranslation>['t']
): string {
  return match(dominant)
    .with('damage', () => t('ui.carPartModal.likelyNeededSoon'))
    .with('service', () => t('ui.carPartModal.likelyNeeded'))
    .with('suggestions', () => {
      const allLikelyFine = operations.every(
        (op) => !op.willNeed && op.percentage >= LIKELY_FINE_PERCENT_THRESHOLD
      );
      return allLikelyFine ? t('rul.likelyFine') : t('rul.worthChecking');
    })
    .exhaustive();
}

export function CarPartModal(props: CarPartModalProps) {
  const {t} = useTranslation();
  const data = props.data;
  const isOpen = isNotNil(data);

  useEscapeKey(isOpen, props.onClose);

  const dominant = isNotNil(data) ? getDominantVariant(data.operations) : null;
  const dominantSubtitle =
    isNotNil(data) && isNotNil(dominant) ? getDominantSubtitle(dominant, data.operations, t) : null;
  const operationCountLabel = isNotNil(data) ? `(${data.operations.length})` : '';
  const Illustration = isNotNil(data) ? getPartIllustration(data.partId) : undefined;
  const showIllustration = isNotNil(Illustration);
  const hasOperations = isNotNil(data) && data.operations.length > 0;

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-y-0 right-0 z-20 flex items-start pr-4 pb-4 pl-4',
        DIAGRAM_CHROME_OFFSET_CLASS,
        'transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
        isOpen ? 'translate-x-0' : 'translate-x-full'
      )}
    >
      <div
        role="complementary"
        aria-label={data?.partName ?? t('ui.carPartModal.partDetails')}
        aria-hidden={!isOpen}
        className="pointer-events-auto flex max-h-full w-[380px] flex-col overflow-y-auto rounded-3xl bg-white shadow-2xl"
      >
        {isNotNil(data) && (
          <>
            <div className="sticky top-0 z-10 flex shrink-0 items-start gap-3 border-b border-gray-100 bg-white px-6 pt-6 pb-5">
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <h2 className="text-2xl leading-tight font-bold text-(--color-text-primary)">
                  {data.partName}
                </h2>
                {isNotNil(dominant) && isNotNil(dominantSubtitle) && (
                  <div className="flex items-center gap-2">
                    <span
                      className={cn('size-2.5 shrink-0 rounded-full', VARIANT_DOT_CLASS[dominant])}
                    />
                    <span className="text-sm text-(--color-text-primary)">
                      {dominantSubtitle}
                      <span className="ml-1 text-(--color-text-secondary)">
                        {operationCountLabel}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={props.onClose}
                aria-label={t('ui.common.close')}
                className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-(--color-text-secondary) transition-colors hover:bg-gray-200"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path
                    d="M1 1L11 11M11 1L1 11"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className={cn('flex flex-col p-6', showIllustration ? 'gap-5' : 'gap-3')}>
              {isNotNil(Illustration) && (
                <PartIllustration Illustration={Illustration} variant={dominant} />
              )}

              {hasOperations && (
                <div
                  role="rowgroup"
                  className={cn('grid w-full gap-y-1.5', TABLE_GRID_COLUMNS_CLASS)}
                  style={OPERATIONS_GRID_STYLE}
                >
                  {data.operations.map((op) => (
                    <OperationRow
                      key={`${op.variant}-${op.description}`}
                      description={op.description}
                      variant={op.variant}
                      percentage={op.percentage}
                      willNeed={op.willNeed}
                      label={op.label}
                    />
                  ))}
                </div>
              )}

              {!hasOperations && (
                <p className="text-sm leading-5 text-(--color-text-secondary)">
                  {t('ui.carPartModal.empty')}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
